/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { Badge } from 'app/components/ui/badge';
import { Button } from 'app/components/atoms/button/button';
import { Input } from 'app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { ProjectShare } from 'app/types/project';
import { Title } from 'app/components/atoms/title/title';
import { Trash2 } from 'lucide-react';
import { useAddProjectShare } from 'app/hooks/requests/projects/use-add-project-share';
import { useDeleteProjectShare } from 'app/hooks/requests/projects/use-delete-project-share';
import { useProjectShares } from 'app/hooks/requests/projects/use-project-shares';
import { useTranslation } from 'react-i18next';
import { useUpdateProjectShare } from 'app/hooks/requests/projects/use-update-project-share';
import React, { useCallback, useState } from 'react';

/**
 * `Props` type.
 */

type Props = {
  projectId: string;
};

/**
 * Export `ProjectShares` component.
 */

export function ProjectShares({ projectId }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { data: shares, isLoading } = useProjectShares(projectId);
  const { mutate: addShare, isLoading: isAdding } = useAddProjectShare(projectId);
  const { mutate: deleteShare } = useDeleteProjectShare(projectId);
  const { mutate: updateShare } = useUpdateProjectShare(projectId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('viewer');

  const handleAdd = useCallback(() => {
    if (!email) {
      return;
    }

    addShare(
      { email, role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('viewer');
        }
      }
    );
  }, [addShare, email, role]);

  const handleRoleChange = useCallback(
    (shareId: number, newRole: string) => {
      updateShare({ shareId, role: newRole });
    },
    [updateShare]
  );

  return (
    <div>
      <Title level={'h4'}>{translate('projects.settings.shares.title')}</Title>

      <div className={'mb-4 flex w-full items-center gap-2'}>
        <Input
          className={'w-[300px]'}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleAdd();
            }
          }}
          placeholder={translate('projects.settings.shares.emailPlaceholder')}
          value={email}
        />

        <Select onValueChange={setRole} value={role}>
          <SelectTrigger className={'w-[120px]'}>
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value={'viewer'}>{'Viewer'}</SelectItem>
            <SelectItem value={'editor'}>{'Editor'}</SelectItem>
          </SelectContent>
        </Select>

        <Button loading={isAdding} onClick={handleAdd} type={'primary'}>
          {translate('projects.settings.shares.add')}
        </Button>
      </div>

      <div className={'overflow-x-auto rounded-md border border-border'}>
        <table className={'w-full text-sm'}>
          <thead>
            <tr className={'border-b border-border text-left text-muted-foreground'}>
              <th className={'px-3 py-2 font-medium'}>{translate('common.labels.email')}</th>

              <th className={'px-3 py-2 font-medium'}>{translate('common.labels.name')}</th>

              <th className={'px-3 py-2 font-medium'}>{'Role'}</th>

              <th className={'w-[60px] px-3 py-2 font-medium'} />
            </tr>
          </thead>

          <tbody>
            {isLoading && (
              <tr>
                <td className={'px-3 py-4 text-muted-foreground'} colSpan={4}>
                  {'...'}
                </td>
              </tr>
            )}

            {!isLoading &&
              (shares ?? []).map((record: ProjectShare) => (
                <tr className={'border-b border-border last:border-b-0'} key={record.id}>
                  <td className={'px-3 py-2'}>{record.user?.email}</td>

                  <td className={'px-3 py-2'}>
                    {record.user ? `${record.user.firstName} ${record.user.lastName}` : '-'}
                  </td>

                  <td className={'px-3 py-2'}>
                    <Select defaultValue={record.role} onValueChange={value => handleRoleChange(record.id, value)}>
                      <SelectTrigger className={'h-8 w-[120px]'}>
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value={'viewer'}>
                          <Badge variant={'secondary'}>{'Viewer'}</Badge>
                        </SelectItem>

                        <SelectItem value={'editor'}>
                          <Badge variant={'secondary'}>{'Editor'}</Badge>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </td>

                  <td className={'px-3 py-2'}>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button danger icon={<Trash2 className={'h-4 w-4'} />} size={'small'} type={'text'} />
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{translate('removeAction')}</AlertDialogTitle>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

                          <AlertDialogAction
                            className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
                            onClick={() => deleteShare(record.id)}
                          >
                            {translate('common.actions.remove')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
